import { type FC, type HTMLAttributes, type MouseEvent, useCallback } from "react";

import { Link, LinkType } from "../../../common/components/Link";
import { useDispatch, useSelector } from "react-redux";
import { selectIsNodeActive, selectNavigationDisplayMode, selectNode } from "../../../Header/redux/navigationSelectors";
import {
  changeNavigationDisplayMode,
  selectNavigationAction,
  selectNavigationImage,
} from "../../../Header/redux/actions";
import { trackNavigationListItem, trackTreesInteraction } from "../../Analytics/ga/ga";
import classNames from "classnames";
import { Icon } from "../../../common/components/icon/Icon";
import { IconType } from "../../../common/components/icon/IconType";
import { IconSize } from "../../../common/components/icon/IconSize";
import { type NavigationNode } from "../../../Header/types";
import { ReduxImperialToggle } from "../../../ImperialMetricData/components/ImperialToggle/ImperialToggle";
import { useTranslation } from "react-i18next";
import { DisplayModeButtonList } from "~/Search/components/common/SearchResultsDisplayMode/SearchResultsDisplayMode";
import { SearchDisplayMode } from "~/Search/models";

const FADE_IN = "scha__fade-in-out__element scha__fade-in-out__fade-in";
const FADE_OUT = "scha__fade-in-out__element scha__fade-in-out__fade-out--abs-position";

interface DesktopNavigationListProps {
  parentNode: NavigationNode;
  active: boolean;
}
export const DesktopNavigationList = ({ parentNode, active }: DesktopNavigationListProps) => {
  let fadeCls;
  if (active) {
    fadeCls = FADE_IN;
  } else {
    fadeCls = FADE_OUT;
  }

  return (
    <div className={fadeCls}>
      <NavigationOverview node={parentNode} navigationLevel={parentNode.level} />
      <ul className="scha__nav__list">
        {parentNode.children.map((childId, index) => (
          <NavigationListItem key={index} index={index} nodeId={childId} />
        ))}
      </ul>
    </div>
  );
};

interface MobileNavigationListProps {
  parentNode: NavigationNode;
}
export const MobileNavigationList = ({ parentNode }: MobileNavigationListProps) => {
  return (
    <ul className="scha__nav__list">
      {parentNode.children.map((childId, index) => (
        <NavigationListItem key={index} index={index} nodeId={childId} />
      ))}
    </ul>
  );
};

interface NavigationOverviewProps {
  node: NavigationNode;
  navigationLevel: number;
}
export const NavigationOverview = ({ node, navigationLevel }: NavigationOverviewProps) => {
  const { t } = useTranslation();

  const content = (
    <>
      <div className="scha__nav__overview__label">{t("subnavigation.overview")}</div>
      <div className="scha__nav__overview__title">{t(node.title)}</div>
      {node.showSeriesTree && <NavigationDisplayModeSelector />}
    </>
  );

  const displayMode = useSelector(selectNavigationDisplayMode);
  const showExtraLine = displayMode === SearchDisplayMode.SERIES_TREE && node.showSeriesTree;

  const cls = classNames("scha__nav__overview", showExtraLine && "scha__nav__overview--series");

  if (node.url) {
    return (
      <Link
        variant={LinkType.SECONDARY}
        className={cls}
        href={node.url}
        target={node.openInNewTab ? "_blank" : undefined}
        data-test-id={`header-navigation-l${navigationLevel}-overview`}
      >
        {content}
      </Link>
    );
  }

  return <div className={cls}>{content}</div>;
};

const NavigationDisplayModeSelector = () => {
  const displayMode = useSelector(selectNavigationDisplayMode);

  const dispatch = useDispatch();

  const changeMode = (mode: SearchDisplayMode, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    let display: "Tile" | "Tree";
    if (mode === SearchDisplayMode.LIST) {
      display = "Tile";
    } else {
      display = "Tree";
    }
    trackTreesInteraction("Navigation", display);
    dispatch(changeNavigationDisplayMode(mode));
  };

  return (
    <div className={"scha__nav__products-action-bar"}>
      <DisplayModeButtonList
        className={"scha__nav__display-mode-section"}
        displayMode={displayMode}
        setDisplayMode={changeMode}
        showTechnical={false}
      />
      {displayMode === SearchDisplayMode.SERIES_TREE && (
        <ReduxImperialToggle className={"scha__nav__products-imperial-toggle"} page="Navigation" />
      )}
    </div>
  );
};

interface NavigationListItemData extends HTMLAttributes<HTMLLIElement> {
  index: number;
  nodeId: number;
}
const NavigationListItem: FC<NavigationListItemData> = ({ nodeId, index, ...liProps }) => {
  const { t } = useTranslation();
  const node = useSelector(selectNode(nodeId));
  const isActive = useSelector(selectIsNodeActive(nodeId));

  const navigationLevel = node.level;
  const hasChildren = node.children && node.children.length > 0;
  const hasUrl = !!node.url;

  const dispatch = useDispatch();

  const selectNav = node.children.length > 0 ? () => dispatch(selectNavigationAction(node)) : undefined;
  const selectImg = () => dispatch(selectNavigationImage(node.bigImage));

  const anchorOnClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      trackNavigationListItem(node.title);
      if (hasChildren) {
        e.preventDefault();
      }
    },
    [hasChildren, liProps.onClick],
  );

  const href = !hasChildren && hasUrl ? node.url : undefined;
  const linkCls = classNames("scha__nav__list__item", isActive && "active");

  return (
    <li data-test-id={`header-navigation-l${navigationLevel}-${index}`} onMouseEnter={selectImg} onClick={selectNav}>
      <Link
        variant={LinkType.NAVIGATION}
        onClick={anchorOnClick}
        href={href}
        target={node.openInNewTab ? "_blank" : undefined}
        className={linkCls}
      >
        <span>{t(node.title)}</span>
        {hasChildren && <Icon type={IconType.ChevronRight} size={IconSize.FONT_24} />}
      </Link>
    </li>
  );
};
